import { useCallback, useEffect } from "react"

import { initCaseResponseInterceptor } from "@/utils/interceptors"
import axios, { AxiosHeaders } from "axios"
import type { AxiosInstance } from "axios"

import type {
  SigmaPaginatedResponse,
  SigmaPaginationOptions,
  SigmaTeam,
  SigmaTeamVisibility,
  SigmaWorkbook,
} from "./sigma.types"
import { defaultSigmaPaginationOptions } from "./sigma.types"
import { useSigmaAccessTokenOwner } from "./useSigmaAccessToken/use-sigma-access-token-owner"

// eslint-disable-next-line import/no-named-as-default-member
export const sigmaHttpClient: AxiosInstance = axios.create({
  baseURL: process.env.SIGMA_API_URL,
})

initCaseResponseInterceptor(sigmaHttpClient)

export interface FetchSigmaTeamsOptions extends Partial<SigmaPaginationOptions> {
  description?: string
  name?: string
  visibility?: SigmaTeamVisibility
}

interface SigmaRetryRequestConfig {
  _sigmaRetried?: boolean
  headers?: AxiosHeaders
}

/**
 * Provides the Sigma api configured with an access token
 *
 * @returns The Sigma api
 *
 * @example
 * const {
 *  fetchSigmaTeams,
 *  fetchSigmaWorkbook,
 *  fetchSigmaWorkbooks,
 *  sigmaAccessTokenIsFetched,
 * } = useSigmaApi()
 */
export const useSigmaApi = () => {
  const { refreshSigmaToken, sigmaAccessTokenData, sigmaAccessTokenIsFetched } =
    useSigmaAccessTokenOwner()

  // Update request interceptor with latest access token
  useEffect(() => {
    const requestInterceptor = sigmaHttpClient.interceptors.request.use(
      (config) => {
        const shouldPreserveRefreshedHeader =
          (config as SigmaRetryRequestConfig)._sigmaRetried &&
          Boolean(config.headers.Authorization)

        if (!shouldPreserveRefreshedHeader) {
          config.headers.Authorization = `Bearer ${sigmaAccessTokenData?.accessToken}`
        }
        config.headers.accept = "application/json"

        return config
      }
    )

    // Clean up previous request interceptor so we're not adding infinitely
    return () => {
      sigmaHttpClient.interceptors.request.eject(requestInterceptor)
    }
  }, [sigmaAccessTokenData?.accessToken])

  useEffect(() => {
    const responseInterceptor = sigmaHttpClient.interceptors.response.use(
      (response) => response,
      async (error: unknown) => {
        const originalRequest = (error as { config?: SigmaRetryRequestConfig })
          .config
        const statusCode = (error as { response?: { status?: number } })
          .response?.status

        if (
          statusCode === 401 &&
          originalRequest &&
          !originalRequest._sigmaRetried
        ) {
          originalRequest._sigmaRetried = true

          const refreshedToken = await refreshSigmaToken()

          if (refreshedToken?.accessToken) {
            originalRequest.headers ??= new AxiosHeaders()
            originalRequest.headers.setAuthorization(
              `Bearer ${refreshedToken.accessToken}`
            )

            return sigmaHttpClient(originalRequest)
          }
        }

        throw error instanceof Error
          ? error
          : new Error("Sigma request failed without an Error object")
      }
    )

    return () => {
      sigmaHttpClient.interceptors.response.eject(responseInterceptor)
    }
  }, [refreshSigmaToken])

  const fetchSigmaTeams = useCallback(
    async (
      options: FetchSigmaTeamsOptions = {}
    ): Promise<SigmaPaginatedResponse<SigmaTeam> | null> => {
      try {
        if (sigmaAccessTokenIsFetched) {
          const response =
            await sigmaHttpClient.get<SigmaPaginatedResponse<SigmaTeam> | null>(
              `/teams`,
              {
                params: { ...defaultSigmaPaginationOptions, ...options },
              }
            )

          return response.data
        }
      } catch (error: unknown) {
        console.error(error)
        return null
      }

      return null
    },
    [sigmaAccessTokenIsFetched]
  )

  const fetchSigmaWorkbook = useCallback(
    async (
      workbookId: SigmaWorkbook["workbookId"]
    ): Promise<SigmaWorkbook | null> => {
      try {
        if (sigmaAccessTokenIsFetched) {
          const response = await sigmaHttpClient.get<SigmaWorkbook>(
            `/workbooks/${workbookId}`
          )

          return response.data
        }
      } catch (error: unknown) {
        console.error(error)
        return null
      }

      return null
    },
    [sigmaAccessTokenIsFetched]
  )

  const fetchSigmaWorkbooks =
    useCallback(async (): Promise<SigmaPaginatedResponse<SigmaWorkbook> | null> => {
      try {
        if (sigmaAccessTokenIsFetched) {
          const response = await sigmaHttpClient.get<
            SigmaPaginatedResponse<SigmaWorkbook>
          >("/workbooks", { params: { limit: 500 } })

          return {
            ...response.data,
            entries: response.data.entries.map((entry) => ({
              ...entry,
              id: entry.workbookId,
            })),
          }
        }
      } catch (error: unknown) {
        console.error(error)
        return null
      }

      return null
    }, [sigmaAccessTokenIsFetched])

  return {
    fetchSigmaTeams,
    fetchSigmaWorkbook,
    fetchSigmaWorkbooks,
    sigmaAccessTokenIsFetched,
  }
}
